import { useState, useEffect } from 'react';

import moment from 'moment';

import logger from '../../config/app.logger';
import { getExtendedLogger } from '../Logger';

import { ABBREVIATED_MONTHS, LOGGER_NAME } from './constants';
import { DEFAULT_LOCALE } from '../Intl/constants';

const log = getExtendedLogger(LOGGER_NAME, logger);

function normalizeLocale(locale: string): string {
  return locale.toLowerCase();
}

function isEnglishLocale(locale: string): boolean {
  return locale === DEFAULT_LOCALE || locale.startsWith(`${DEFAULT_LOCALE}-`);
}

function applyCustomMonths(): void {
  const currentLocale = moment.locale();
  moment.updateLocale(currentLocale, {
    monthsShort: ABBREVIATED_MONTHS,
  });
}

// TODO : Temporary fix to prevent moment locale update, needs to be removed, once we have single moment instance across the app.
let _localeUpdateBlocked = false;
function blockMomentLocaleUpdates(expectedLocale: string): void {
  if (_localeUpdateBlocked) return;
  _localeUpdateBlocked = true;

  const originalLocale = moment.locale;
  const originalUpdateLocale = moment.updateLocale;

  (moment as any).locale = function (...args: any[]): string {
    log(
      '[moment.locale] caller: %s, args: %o',
      new Error().stack?.split('\n')[2]?.trim(),
      args
    );
    if (args.length > 0 && (args[0] || '').toLowerCase() !== expectedLocale) {
      return originalLocale();
    }
    return originalLocale(...args);
  };

  (moment as any).updateLocale = function (locale: string, config: any): any {
    log(
      '[moment.updateLocale] caller: %s, locale: %s',
      new Error().stack?.split('\n')[2]?.trim(),
      locale
    );
    if ((locale || '').toLowerCase() === expectedLocale) {
      return;
    }
    const result = originalUpdateLocale(locale, config);
    if (originalLocale() !== expectedLocale) {
      originalLocale(expectedLocale);
    }
    return result;
  };
}

async function loadMomentLocale(locale: string): Promise<void> {
  if (isEnglishLocale(locale)) {
    moment.locale(locale);
    applyCustomMonths();
    return;
  }
  try {
    await import(`moment/locale/${locale}`);
    moment.locale(locale);
    blockMomentLocaleUpdates(locale);
  } catch {
    moment.locale(DEFAULT_LOCALE);
    applyCustomMonths();
  }
}

export function useMomentLocale(locale: string | undefined, isAdmin?: boolean) {
  const normalizedLocale = normalizeLocale(isAdmin ? DEFAULT_LOCALE : locale || DEFAULT_LOCALE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    log('loading moment locale: %s', normalizedLocale);
    loadMomentLocale(normalizedLocale).finally(() => {
      if (!cancelled) {
        log('moment locale loaded: %s', normalizedLocale);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [normalizedLocale]);

  return { loading };
}
